import { Icon, ItemPackageSkeleton } from "@dmm-com/rocket-web-ui-library";
import { useState, useRef, forwardRef, memo, useCallback } from "react";

import {
  PC_CAROUSEL_MARGIN,
  PC_ITEM_LEFT_MARGIN,
  SP_CAROUSEL_MARGIN,
  SP_RANKING_ITEM_LEFT_MARGIN,
  SP_ITEM_LEFT_MARGIN,
  PC_RANKING_ITEM_LEFT_MARGIN,
} from "./const";
import * as Styled from "./style";
import { useCarousel } from "./use-carousel";
import { CarouselItem } from "~/components/blocks/common/carousel/carousel-item";

import type { ItemPackageProps } from "~/components/blocks/common/item-package/type";
import { useHandleClickByDevice } from "~/hooks/use-handle-click-by-device";
import { useMatchMedia } from "~/hooks/use-match-media";

type SkeletonSetting = {
  /** スケルトンを表示するかどうか */
  isShowSkeleton: boolean;
  /** 表示する画像のアスペクト比 */
  aspectRatio: string;
  /** ItemPackageのmainText領域のスケルトンを出すかどうか */
  isShowMainText?: boolean;
  /** ItemPackageのsubText領域のスケルトンを出すかどうか */
  isShowSubText?: boolean;
};

export type CarouselProps = {
  /** カルーセル内に表示するアイテムのProps */
  items: Omit<ItemPackageProps, "ref">[];
  /**
   * 画面幅に表示するアイテムの個数
   * - Storybookで設定の仕方を参考にしてください
   * - 例: コンテナの幅 / PCの場合の最小アイテムサイズ = displayItemCount
   * - 例: コンテナの幅 / SPの場合の最小アイテムサイズ = displayItemCount
   */
  displayItemCount: number;
  /**
   * HACK:イケてない
   * - カルーセルで表示する要素がRankingTypeだった場合に限り、余白の取り方を変更する
   * - PCモード 8px ⇨ 12px
   * - SPモード 4px ⇨ 8px
   */
  isRankingType?: boolean;
  /**
   * ユニークID
   * Carousel内部要素のHover離脱検知 & previewModalの余白高さ計算 で利用
   */
  uniqueId?: string;
  /**
   * カルーセルアイテムのItemPackageでPreviewModalを出すかどうか
   * modalではPreviewModalを出さないようにするために利用
   */
  isDisabledPreviewModal?: boolean;
  /**
   * スケルトンの表示設定
   */
  skeletonSetting: SkeletonSetting;
  /**
   * カルーセルの最初のitem左の余白
   */
  isFirstLeftMargin?: boolean;
} & JSX.IntrinsicElements["div"];

export const Carousel = memo(
  forwardRef<HTMLDivElement, CarouselProps>((props, ref) => {
    const {
      items,
      displayItemCount,
      isRankingType,
      uniqueId = "0",
      isDisabledPreviewModal = false,
      skeletonSetting,
      isFirstLeftMargin = true,
      ...args
    } = props;

    const resizeWatchRef = useRef<HTMLDivElement>(null);

    const { isShowNextButton, isShowPrevButton, onNextButtonClicked, onPrevButtonClicked, margin } = useCarousel({
      resizeWatchRef,
      displayItemCount,
      uniqueId,
      isRankingType,
    });

    const [showPreviewModal, setShowPreviewModal] = useState(false);
    const { isPcSize, isTabletSize, isSpSize } = useMatchMedia();
    const { handleClickByDevice } = useHandleClickByDevice();
    const canShowPreviewModal = isPcSize || isTabletSize;
    const shouldShowPreviewModal = canShowPreviewModal && !isDisabledPreviewModal;
    const carouselMarginSize = isSpSize ? SP_CAROUSEL_MARGIN : PC_CAROUSEL_MARGIN;
    const itemWidthCalc =
      carouselMarginSize * 2 +
      (displayItemCount - 1) *
        (isRankingType
          ? isSpSize
            ? SP_RANKING_ITEM_LEFT_MARGIN
            : PC_RANKING_ITEM_LEFT_MARGIN
          : isSpSize
          ? SP_ITEM_LEFT_MARGIN
          : PC_ITEM_LEFT_MARGIN);

    const itemHoverHandler = useCallback(
      (hoverState: boolean) => {
        if (!shouldShowPreviewModal) return;
        setShowPreviewModal(hoverState);
      },

      [shouldShowPreviewModal]
    );

    const onItemHover = shouldShowPreviewModal ? itemHoverHandler : undefined;

    const canShowNextButton = isShowNextButton && displayItemCount < items.length;

    return (
      <Styled.Wrapper {...args}>
        <Styled.DivCarouselWrapper
          ref={ref}
          margin={margin}
          showPreviewModal={showPreviewModal}
          role="group"
          id="carousel-list"
        >
          {isShowPrevButton && !isSpSize && (
            <Styled.BtnPrev
              margin={margin}
              showPreviewModal={showPreviewModal}
              type="button"
              name="prev button"
              aria-label="前へ"
              aria-controls="carousel-list"
              {...handleClickByDevice<HTMLButtonElement>(() => {
                onPrevButtonClicked();
              })}
            >
              <Icon name="arrowLeft" size="XL" color={"GRAY900"} />
            </Styled.BtnPrev>
          )}
          <Styled.DivCarouseItem ref={resizeWatchRef} margin={margin} showPreviewModal={showPreviewModal}>
            {!skeletonSetting.isShowSkeleton
              ? items.map((item, idx) => (
                  <CarouselItem
                    key={idx}
                    displayItemCount={displayItemCount}
                    itemWidthCalc={itemWidthCalc}
                    isRankingType={!!isRankingType}
                    onItemHover={onItemHover}
                    className={`carousel-item-${uniqueId}`}
                    shouldShowPreviewModal={shouldShowPreviewModal}
                    uniqueId={uniqueId}
                    isFirstLeftMargin={isFirstLeftMargin}
                    {...item}
                  />
                ))
              : [...Array(displayItemCount + 1)].map((_, idx) => (
                  <Styled.DivItem
                    key={idx}
                    displayItemCount={displayItemCount}
                    itemWidthCalc={itemWidthCalc}
                    isRankingType={!!isRankingType}
                    isFirstLeftMargin={isFirstLeftMargin}
                  >
                    <ItemPackageSkeleton
                      className=""
                      width="100%"
                      aspectRatio={skeletonSetting.aspectRatio}
                      isShowMainText={skeletonSetting.isShowMainText}
                      isShowSubText={skeletonSetting.isShowSubText}
                      delay={idx}
                    />
                  </Styled.DivItem>
                ))}
          </Styled.DivCarouseItem>
          {canShowNextButton && !isSpSize && (
            <Styled.BtnNext
              margin={margin}
              showPreviewModal={showPreviewModal}
              type="button"
              name="next button"
              aria-label="次へ"
              aria-controls="carousel-list"
              {...handleClickByDevice<HTMLButtonElement>(() => {
                onNextButtonClicked();
              })}
            >
              <Icon name="arrowRight" size="XL" color="GRAY900" />
            </Styled.BtnNext>
          )}
        </Styled.DivCarouselWrapper>
      </Styled.Wrapper>
    );
  })
);

Carousel.displayName = "Carousel";
